import { useMemo, useState } from 'react';
import * as material from '@mui/material';

import { Button } from '../../../shared/Button';
import { Icon } from '../../../shared/Icon';
import { TocaIcon, TocaIconMap } from '../../model/components/TocaIcon';

import styles from './TocaIconInput.module.scss';

export interface TocaIconInputProps {
    selectedIcon: TocaIcon | '';
    onIconChange: (icon: TocaIcon) => void;
    label?: string;
    className?: string;
    readonly?: boolean;
}

export const TocaIconInput = ({
    selectedIcon,
    onIconChange,
    label,
    className,
    readonly = false,
}: TocaIconInputProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const ICONS_PER_PAGE = 30;

    const filteredIcons = useMemo(() => {
        const allIcons = Object.keys(TocaIconMap);
        if (!searchTerm) return allIcons;

        return allIcons.filter((icon) =>
            icon.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }, [searchTerm]);

    const paginatedIcons = useMemo(() => {
        const startIndex = (currentPage - 1) * ICONS_PER_PAGE;
        const endIndex = startIndex + ICONS_PER_PAGE;
        return filteredIcons.slice(startIndex, endIndex);
    }, [filteredIcons, currentPage]);

    const totalPages = Math.ceil(filteredIcons.length / ICONS_PER_PAGE);

    const handleIconSelect = (icon: TocaIcon) => {
        onIconChange(icon);
        setIsModalOpen(false);
        setSearchTerm('');
        setCurrentPage(1);
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1); // Reset to first page when searching
    };

    return (
        <>
            <div style={{ position: 'relative', display: 'inline-block' }}>
                <Button
                    onClick={readonly ? undefined : () => setIsModalOpen(true)}
                    disabled={readonly}
                    className={`${styles.button} ${className || ''}`}
                    style={{
                        backgroundColor: selectedIcon ? '#D8D4EC' : undefined,
                        borderColor: selectedIcon ? '#7C70C0' : undefined,
                        cursor: readonly ? 'not-allowed' : undefined,
                        opacity: readonly ? 0.6 : undefined,
                    }}
                >
                    {selectedIcon ? (
                        <img
                            src={TocaIconMap[selectedIcon]}
                            alt={selectedIcon}
                            className={styles.buttonImage}
                        />
                    ) : (
                        'Select'
                    )}
                </Button>
                {readonly && (
                    <Icon
                        name="lock"
                        style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            fontSize: '16px',
                            color: '#666',
                            backgroundColor: 'white',
                            borderRadius: '2px',
                            padding: '2px',
                        }}
                    />
                )}
            </div>

            {isModalOpen && !readonly && (
                <material.Modal
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    aria-labelledby="emoji-modal-title"
                >
                    <material.Box className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h3
                                id="emoji-modal-title"
                                className={styles.modalTitle}
                            >
                                Choose {label || 'Icon'}
                            </h3>
                            <div className={styles.searchContainer}>
                                <material.Autocomplete
                                    options={Object.keys(TocaIconMap)}
                                    value={null}
                                    onChange={(_, newValue) => {
                                        handleSearchChange(newValue || '');
                                    }}
                                    inputValue={searchTerm}
                                    onInputChange={(_, newInputValue) => {
                                        handleSearchChange(newInputValue);
                                    }}
                                    renderInput={(params) => (
                                        <material.TextField
                                            {...params}
                                            placeholder="search"
                                            variant="outlined"
                                            size="small"
                                            className={styles.searchInput}
                                        />
                                    )}
                                    className={styles.autocomplete}
                                    freeSolo
                                />
                            </div>
                        </div>
                        <div className={styles.iconsGrid}>
                            {paginatedIcons.map((icon) => (
                                <material.Tooltip
                                    key={icon}
                                    title={icon}
                                    placement="top"
                                >
                                    <material.Box
                                        component="button"
                                        type="button"
                                        onClick={() => handleIconSelect(icon)}
                                        className={`${styles.iconButton} ${
                                            selectedIcon === icon
                                                ? styles.iconButtonSelected
                                                : ''
                                        }`}
                                    >
                                        <img
                                            src={TocaIconMap[icon]}
                                            alt={icon}
                                            className={styles.iconImage}
                                        />
                                    </material.Box>
                                </material.Tooltip>
                            ))}
                        </div>
                        {totalPages > 1 && (
                            <div className={styles.paginationContainer}>
                                <material.Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={(_, page) => setCurrentPage(page)}
                                    color="primary"
                                    size="small"
                                    showFirstButton
                                    showLastButton
                                />
                            </div>
                        )}
                    </material.Box>
                </material.Modal>
            )}
        </>
    );
};
